import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { storage } from '../utils/storage';
import { db } from '../config/firebase';
import { 
  doc, 
  getDoc, 
  collection, 
  query, 
  where, 
  orderBy, 
  getDocs, 
  Timestamp 
} from 'firebase/firestore';
import { format, startOfDay, endOfDay, startOfMonth, endOfMonth, subDays } from 'date-fns';
import { ptBR } from 'date-fns/locale/pt-BR';
import { walletService } from '../services/walletService';

type Periodo = 'hoje' | 'semana' | 'mes' | 'custom';

interface EntregaItem {
  id: string;
  motoboyNome?: string;
  motoboyId?: string;
  setor?: string;
  cliente?: string;
  frete?: number;
  taxaCentral?: number;
  entregueEm?: Timestamp;
  finalizadoEm?: Timestamp;
  aceitaEm?: Timestamp;
  chegou_empresaEm?: Timestamp;
  em_rotaEm?: Timestamp;
  numeroPedido?: number;
  numeroGlobal?: number;
}

interface GrupoEntregador {
  tipo: 'header';
  entregadorNome: string;
  entregadorId: string;
  totalEntregas: number;
  totalValor: number;
}

type ItemLista = EntregaItem | GrupoEntregador;

const HistoricoEntregas: React.FC = () => {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [empresaId, setEmpresaId] = useState<string>('');
  const [isLoading, setIsLoading] = useState(true);
  const [periodo, setPeriodo] = useState<Periodo>('hoje');
  const [customInicio, setCustomInicio] = useState<Date | null>(null);
  const [customFim, setCustomFim] = useState<Date | null>(null);
  const [saldo, setSaldo] = useState<number>(0);
  const [entregas, setEntregas] = useState<EntregaItem[]>([]);
  const [gruposExpandidos, setGruposExpandidos] = useState<Set<string>>(new Set());
  const [showMenu, setShowMenu] = useState(false);
  const [empresaNome, setEmpresaNome] = useState<string>('');
  const [empresaFoto, setEmpresaFoto] = useState<string>('');

  const formatBRL = (valor: number) => {
    try {
      return valor.toLocaleString('pt-BR', {
        style: 'currency',
        currency: 'BRL',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    } catch {
      const num = Number(valor || 0);
      return `R$ ${num.toFixed(2).replace('.', ',')}`;
    }
  };

  const datas = useMemo(() => {
    const hoje = new Date();
    if (periodo === 'hoje') return { inicio: startOfDay(hoje), fim: endOfDay(hoje) };
    if (periodo === 'semana') return { inicio: startOfDay(subDays(hoje, 6)), fim: endOfDay(hoje) };
    if (periodo === 'custom') {
      const inicio = startOfDay(customInicio || hoje);
      const fim = endOfDay(customFim || hoje);
      return { inicio, fim };
    }
    return { inicio: startOfMonth(hoje), fim: endOfMonth(hoje) };
  }, [periodo, customInicio, customFim]);

  const labelPeriodo = useMemo(() => {
    if (periodo === 'hoje') return `Hoje - ${format(datas.inicio, 'dd/MM/yyyy', { locale: ptBR })}`;
    if (periodo === 'semana') return `Últimos 7 dias - ${format(datas.inicio, 'dd/MM/yyyy', { locale: ptBR })} até ${format(datas.fim, 'dd/MM/yyyy', { locale: ptBR })}`;
    if (periodo === 'mes') return `Mês atual - ${format(datas.inicio, 'MMMM yyyy', { locale: ptBR })}`;
    return `${format(datas.inicio, 'dd/MM/yyyy', { locale: ptBR })} até ${format(datas.fim, 'dd/MM/yyyy', { locale: ptBR })}`;
  }, [periodo, datas]);

  // Carregar empresaId
  useEffect(() => {
    const carregarEmpresa = async () => {
      try {
        const userId = await storage.getItem('userId');
        if (!userId) {
          navigate('/login');
          return;
        }

        const userDoc = await getDoc(doc(db, 'usuarios', userId));
        if (userDoc.exists()) {
          const userData = userDoc.data();
          const empresaId = userData.empresaId;
          
          if (empresaId) {
            setEmpresaId(empresaId);
            
            // Carregar dados da empresa
            const empresaDoc = await getDoc(doc(db, 'Empresa', empresaId));
            if (empresaDoc.exists()) {
              const empresaData = empresaDoc.data();
              setEmpresaNome(empresaData.nomeEmpresa || empresaData.nome || 'Empresa');
              setEmpresaFoto(empresaData.foto || empresaData.fotoUrl || '');
            }
          }
        }
      } catch (error) {
        console.error('Erro ao carregar empresa:', error);
      }
    };
    
    carregarEmpresa();
  }, [navigate]);

  // Carregar saldo
  useEffect(() => {
    const carregarSaldo = async () => {
      if (!empresaId) return;
      try {
        const s = await walletService.getAvailableBalance(empresaId);
        setSaldo(s);
      } catch (error) {
        console.error('Erro ao carregar saldo:', error);
      }
    };
    carregarSaldo();
  }, [empresaId]);

  // Buscar entregas
  useEffect(() => {
    const carregar = async () => {
      if (!empresaId) return;
      setIsLoading(true);
      try {
        const empresaRef = doc(db, 'Empresa', empresaId);
        const { inicio, fim } = datas;
        const inicioTs = Timestamp.fromDate(inicio);
        const fimTs = Timestamp.fromDate(fim);

        let itens: any[] = [];

        if (periodo === 'hoje') {
          // Buscar em 'entregues' para hoje
          try {
            const colRefEntregues = collection(empresaRef, 'entregues');
            const qEnt = query(
              colRefEntregues, 
              where('entregueEm', '>=', inicioTs),
              where('entregueEm', '<=', fimTs),
              orderBy('entregueEm', 'desc')
            );
            const snapEnt = await getDocs(qEnt);
            itens = snapEnt.docs.map(d => ({ id: d.id, ...d.data() })) as any[];
            
            // Filtro adicional para garantir apenas entregas do dia atual
            const hoje = new Date();
            const inicioDia = startOfDay(hoje);
            const fimDia = endOfDay(hoje);
            
            itens = itens.filter((item: any) => {
              const dataEntrega = item.entregueEm?.toDate?.() || 
                                 item.aceitaEm?.toDate?.() || 
                                 item.chegou_empresaEm?.toDate?.() || 
                                 item.em_rotaEm?.toDate?.() || null;
              
              if (!dataEntrega) return false;
              return dataEntrega >= inicioDia && dataEntrega <= fimDia;
            });
          } catch (errorEntregues) {
            console.error('Erro ao buscar em entregues:', errorEntregues);
            itens = [];
          }
        } else {
          // Para outros períodos, usar 'entregas_finalizadas'
          try {
            const colRefFinalizadas = collection(empresaRef, 'entregas_finalizadas');
            const qFin = query(
              colRefFinalizadas,
              where('finalizadoEm', '>=', inicioTs),
              where('finalizadoEm', '<=', fimTs),
              orderBy('finalizadoEm', 'desc')
            );
            const snapFinalizadas = await getDocs(qFin);
            itens = snapFinalizadas.docs.map(d => ({ id: d.id, ...d.data() })) as any[];

            // Fallback: se vazio, buscar em 'entregues'
            if (itens.length === 0) {
              try {
                const colRefEntregues = collection(empresaRef, 'entregues');
                const qEnt = query(
                  colRefEntregues, 
                  where('entregueEm', '>=', inicioTs),
                  where('entregueEm', '<=', fimTs),
                  orderBy('entregueEm', 'desc')
                );
                const snapEnt = await getDocs(qEnt);
                itens = snapEnt.docs.map(d => ({ id: d.id, ...d.data() })) as any[];
              } catch (errorEntregues) {
                console.error('Erro ao buscar em entregues (fallback):', errorEntregues);
              }
            }
          } catch (errorFinalizadas) {
            console.error('Erro ao buscar em entregas_finalizadas:', errorFinalizadas);
            itens = [];
          }
        }

        // Resolver nome do motoboy quando ausente
        try {
          const idsFaltantes = Array.from(new Set(
            itens
              .filter((e: any) => (!e.motoboyNome || String(e.motoboyNome).trim() === '') && e.motoboyId)
              .map((e: any) => String(e.motoboyId))
          ));
          
          if (idsFaltantes.length > 0) {
            const idParaNome = new Map<string, string>();
            await Promise.all(idsFaltantes.map(async (id) => {
              try {
                const snap = await getDoc(doc(db, 'usuarios', id));
                if (snap.exists()) {
                  const data: any = snap.data();
                  const nome = data?.nome || '';
                  if (nome) idParaNome.set(id, nome);
                }
              } catch {}
            }));
            
            if (idParaNome.size > 0) {
              itens = itens.map((e: any) => {
                if ((!e.motoboyNome || String(e.motoboyNome).trim() === '') && e.motoboyId) {
                  const nome = idParaNome.get(String(e.motoboyId));
                  return nome ? { ...e, motoboyNome: nome } : e;
                }
                return e;
              });
            }
          }
        } catch {}

        setEntregas(itens);
      } catch (e) {
        console.error('Erro ao carregar entregas:', e);
        setEntregas([]);
      } finally {
        setIsLoading(false);
      }
    };
    carregar();
  }, [empresaId, datas, periodo]);

  // KPIs
  const totalEntregas = entregas.length;
  const totalPagamentos = useMemo(() => entregas.reduce((s, e: any) => {
    const frete = Number(e.frete || e.valor || 0);
    const taxa = Number(e.taxaCentral || 0);
    return s + (isNaN(frete) ? 0 : frete) + (isNaN(taxa) ? 0 : taxa);
  }, 0), [entregas]);

  // Agrupar entregas por entregador
  const dadosAgrupados = useMemo(() => {
    const grupos = new Map<string, EntregaItem[]>();
    
    entregas.forEach((entrega) => {
      const nomeEntregador = entrega.motoboyNome || 'Sem nome';
      const idEntregador = entrega.motoboyId || 'sem-id';
      const chave = `${idEntregador}|${nomeEntregador}`;
      
      if (!grupos.has(chave)) {
        grupos.set(chave, []);
      }
      grupos.get(chave)!.push(entrega);
    });

    const itens: ItemLista[] = [];
    
    Array.from(grupos.entries())
      .sort(([a], [b]) => {
        const nomeA = a.split('|')[1];
        const nomeB = b.split('|')[1];
        return nomeA.localeCompare(nomeB);
      })
      .forEach(([chave, entregasGrupo]) => {
        const [idEntregador, nomeEntregador] = chave.split('|');
        const grupoKey = `${idEntregador}|${nomeEntregador}`;
        const estaExpandido = gruposExpandidos.has(grupoKey);
        
        const totalValor = entregasGrupo.reduce((s, e: any) => {
          const frete = Number(e.frete || e.valor || 0);
          const taxa = Number(e.taxaCentral || 0);
          return s + (isNaN(frete) ? 0 : frete) + (isNaN(taxa) ? 0 : taxa);
        }, 0);

        itens.push({
          tipo: 'header',
          entregadorNome: nomeEntregador,
          entregadorId: idEntregador,
          totalEntregas: entregasGrupo.length,
          totalValor,
        } as GrupoEntregador);

        if (estaExpandido) {
          entregasGrupo
            .sort((a, b) => {
              const dataA = a.entregueEm?.toDate?.() || 
                           a.finalizadoEm?.toDate?.() || 
                           a.aceitaEm?.toDate?.() || 
                           new Date(0);
              const dataB = b.entregueEm?.toDate?.() || 
                           b.finalizadoEm?.toDate?.() || 
                           b.aceitaEm?.toDate?.() || 
                           new Date(0);
              return dataB.getTime() - dataA.getTime();
            })
            .forEach((entrega) => {
              itens.push(entrega);
            });
        }
      });

    return itens;
  }, [entregas, gruposExpandidos]);

  const toggleGrupo = (grupoKey: string) => {
    setGruposExpandidos((prev) => {
      const novo = new Set(prev);
      if (novo.has(grupoKey)) {
        novo.delete(grupoKey);
      } else {
        novo.add(grupoKey);
      }
      return novo;
    });
  };

  const exportarPDF = () => {
    // Para web, vamos abrir uma nova janela com o conteúdo formatado
    const html = `
      <!DOCTYPE html>
      <html>
        <head>
          <meta charset="utf-8" />
          <title>Relatório de Entregas</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 24px; }
            h1 { font-size: 20px; margin: 0 0 4px 0; }
            h2 { font-size: 14px; color: #555; margin: 0 0 16px 0; }
            table { width: 100%; border-collapse: collapse; margin-top: 16px; }
            th, td { border: 1px solid #ddd; padding: 8px; font-size: 12px; }
            th { background: #f5f5f5; text-align: left; }
            .resumo { background: #f8f9fa; padding: 16px; border-radius: 8px; margin-bottom: 16px; }
            .resumo-item { display: inline-block; margin-right: 24px; }
          </style>
        </head>
        <body>
          <h1>Relatório de Entregas</h1>
          <h2>${labelPeriodo}</h2>
          <div class="resumo">
            <div class="resumo-item"><strong>Total Pago:</strong> ${formatBRL(totalPagamentos)}</div>
            <div class="resumo-item"><strong>Entregas:</strong> ${totalEntregas}</div>
          </div>
          <table>
            <thead>
              <tr>
                <th>#</th>
                <th>Setor</th>
                <th>Valor</th>
                <th>Data</th>
              </tr>
            </thead>
            <tbody>
              ${entregas.map((e: any, index) => {
                const data = e.entregueEm?.toDate?.() || 
                            e.finalizadoEm?.toDate?.() || 
                            e.aceitaEm?.toDate?.() || null;
                const valor = Number(e.frete || e.valor || 0) + Number(e.taxaCentral || 0);
                return `
                  <tr>
                    <td style="text-align: center;">${index + 1}</td>
                    <td>${e.setor || 'N/A'}</td>
                    <td>${formatBRL(valor)}</td>
                    <td>${data ? format(data, 'dd/MM/yyyy HH:mm', { locale: ptBR }) : '--'}</td>
                  </tr>
                `;
              }).join('')}
            </tbody>
          </table>
        </body>
      </html>
    `;
    
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(html);
      printWindow.document.close();
      printWindow.print();
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  if (isLoading && !empresaId) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Carregando histórico...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-lg border-b border-gray-200/50 shadow-sm sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            <div className="flex items-center gap-4">
              <button
                onClick={() => setShowMenu(true)}
                className="p-2 text-gray-700 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              </button>
              <div>
                <h1 className="text-xl font-bold text-gray-900">Histórico de Entregas</h1>
                <p className="text-sm text-gray-500">Relatório de pagamentos</p>
              </div>
            </div>
            <button
              onClick={() => navigate('/empresa')}
              className="text-blue-600 hover:text-blue-800 flex items-center gap-2 px-4 py-2 hover:bg-blue-50 rounded-lg transition-colors"
            >
              <span>←</span>
              <span>Voltar</span>
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 lg:px-8 py-8">
        {/* Filtros de Período */}
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
          <h2 className="text-lg font-bold text-gray-900 mb-4">Período</h2>
          <div className="flex flex-wrap gap-3">
            <button
              onClick={() => setPeriodo('hoje')}
              className={`px-6 py-3 rounded-xl font-semibold transition-all ${
                periodo === 'hoje'
                  ? 'bg-blue-600 text-white shadow-lg'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Hoje
            </button>
            <button
              onClick={() => setPeriodo('semana')}
              className={`px-6 py-3 rounded-xl font-semibold transition-all ${
                periodo === 'semana'
                  ? 'bg-blue-600 text-white shadow-lg'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Últimos 7 dias
            </button>
            <button
              onClick={() => setPeriodo('mes')}
              className={`px-6 py-3 rounded-xl font-semibold transition-all ${
                periodo === 'mes'
                  ? 'bg-blue-600 text-white shadow-lg'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Mês atual
            </button>
            <button
              onClick={() => {
                setPeriodo('custom');
                if (!customInicio) setCustomInicio(new Date());
                if (!customFim) setCustomFim(new Date());
              }}
              className={`px-6 py-3 rounded-xl font-semibold transition-all ${
                periodo === 'custom'
                  ? 'bg-blue-600 text-white shadow-lg'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Personalizado
            </button>
          </div>

          {periodo === 'custom' && (
            <div className="mt-4 grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Data Início</label>
                <input
                  type="date"
                  value={customInicio ? format(customInicio, 'yyyy-MM-dd') : ''}
                  onChange={(e) => setCustomInicio(e.target.value ? new Date(e.target.value) : null)}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all text-gray-900"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Data Fim</label>
                <input
                  type="date"
                  value={customFim ? format(customFim, 'yyyy-MM-dd') : ''}
                  onChange={(e) => setCustomFim(e.target.value ? new Date(e.target.value) : null)}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all text-gray-900"
                />
              </div>
            </div>
          )}

          <div className="mt-4">
            <p className="text-sm text-gray-600">{labelPeriodo}</p>
          </div>
        </div>

        {/* KPIs */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl shadow-lg p-6 text-white">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm opacity-90">Saldo Disponível</span>
              <span className="text-2xl">💰</span>
            </div>
            <p className="text-2xl font-bold">{formatBRL(saldo)}</p>
          </div>

          <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl shadow-lg p-6 text-white">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm opacity-90">Total Entregas</span>
              <span className="text-2xl">📦</span>
            </div>
            <p className="text-2xl font-bold">{totalEntregas}</p>
          </div>

          <div className="bg-gradient-to-br from-red-500 to-red-600 rounded-xl shadow-lg p-6 text-white">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm opacity-90">Total Pago</span>
              <span className="text-2xl">💸</span>
            </div>
            <p className="text-2xl font-bold">{formatBRL(totalPagamentos)}</p>
          </div>

          <button
            onClick={exportarPDF}
            className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl shadow-lg p-6 text-white hover:from-purple-600 hover:to-purple-700 transition-all flex flex-col items-center justify-center"
          >
            <span className="text-2xl mb-2">📄</span>
            <span className="font-semibold">Exportar PDF</span>
          </button>
        </div>

        {/* Lista de Entregas Agrupadas */}
        <div className="bg-white rounded-2xl shadow-lg p-6">
          <h2 className="text-lg font-bold text-gray-900 mb-4">Pagamentos de Entregas</h2>
          
          {dadosAgrupados.length === 0 ? (
            <div className="text-center py-12">
              <div className="text-6xl mb-4">📦</div>
              <p className="text-gray-600 text-lg">Nenhuma entrega encontrada no período</p>
            </div>
          ) : (
            <div className="space-y-4">
              {dadosAgrupados.map((item, index) => {
                if ('tipo' in item && item.tipo === 'header') {
                  const grupo = item as GrupoEntregador;
                  const grupoKey = `${grupo.entregadorId}|${grupo.entregadorNome}`;
                  const estaExpandido = gruposExpandidos.has(grupoKey);
                  
                  return (
                    <div key={`header-${grupo.entregadorId}-${index}`} className="border-2 border-blue-200 rounded-xl overflow-hidden">
                      <button
                        onClick={() => toggleGrupo(grupoKey)}
                        className="w-full bg-gradient-to-r from-blue-50 to-indigo-50 p-4 flex items-center justify-between hover:from-blue-100 hover:to-indigo-100 transition-all"
                      >
                        <div className="flex items-center gap-3">
                          <svg
                            className={`w-5 h-5 text-blue-600 transform transition-transform ${estaExpandido ? 'rotate-90' : ''}`}
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                          <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center">
                            <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                            </svg>
                          </div>
                          <div className="text-left">
                            <p className="font-bold text-gray-900">{grupo.entregadorNome}</p>
                            <p className="text-sm text-gray-600">
                              {grupo.totalEntregas} {grupo.totalEntregas === 1 ? 'entrega' : 'entregas'}
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="font-bold text-blue-600 text-lg">{formatBRL(grupo.totalValor)}</p>
                        </div>
                      </button>

                      {estaExpandido && (
                        <div className="p-4 bg-gray-50 space-y-2">
                          {entregas
                            .filter(e => 
                              (e.motoboyId === grupo.entregadorId) || 
                              (e.motoboyNome === grupo.entregadorNome && (!e.motoboyId || e.motoboyId === 'sem-id'))
                            )
                            .sort((a, b) => {
                              const dataA = a.entregueEm?.toDate?.() || 
                                           a.finalizadoEm?.toDate?.() || 
                                           a.aceitaEm?.toDate?.() || 
                                           new Date(0);
                              const dataB = b.entregueEm?.toDate?.() || 
                                           b.finalizadoEm?.toDate?.() || 
                                           b.aceitaEm?.toDate?.() || 
                                           new Date(0);
                              return dataB.getTime() - dataA.getTime();
                            })
                            .map((entrega) => {
                              const data = entrega.entregueEm?.toDate?.() || 
                                          entrega.finalizadoEm?.toDate?.() || 
                                          entrega.aceitaEm?.toDate?.() || null;
                              const valor = Number(entrega.frete || 0) + Number(entrega.taxaCentral || 0);
                              
                              return (
                                <div
                                  key={entrega.id}
                                  className="bg-white rounded-lg p-4 border border-gray-200 flex items-center justify-between"
                                >
                                  <div>
                                    <p className="font-semibold text-gray-900">{entrega.setor || 'Setor não informado'}</p>
                                    <p className="text-sm text-gray-600">{entrega.cliente || 'Cliente não informado'}</p>
                                    {data && (
                                      <p className="text-xs text-gray-500 mt-1">
                                        {format(data, 'dd/MM/yyyy HH:mm', { locale: ptBR })}
                                      </p>
                                    )}
                                  </div>
                                  <div className="text-right">
                                    <p className="font-bold text-gray-900">{formatBRL(valor)}</p>
                                    {entrega.numeroPedido && (
                                      <p className="text-xs text-gray-500">#{entrega.numeroPedido}</p>
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                        </div>
                      )}
                    </div>
                  );
                } else {
                  // Item de entrega individual (quando grupo está expandido)
                  const entrega = item as EntregaItem;
                  const data = entrega.entregueEm?.toDate?.() || 
                              entrega.finalizadoEm?.toDate?.() || 
                              entrega.aceitaEm?.toDate?.() || null;
                  const valor = Number(entrega.frete || 0) + Number(entrega.taxaCentral || 0);
                  
                  return (
                    <div
                      key={`entrega-${entrega.id}-${index}`}
                      className="bg-white rounded-lg p-4 border border-gray-200 flex items-center justify-between ml-8"
                    >
                      <div>
                        <p className="font-semibold text-gray-900">{entrega.setor || 'Setor não informado'}</p>
                        <p className="text-sm text-gray-600">{entrega.cliente || 'Cliente não informado'}</p>
                        {data && (
                          <p className="text-xs text-gray-500 mt-1">
                            {format(data, 'dd/MM/yyyy HH:mm', { locale: ptBR })}
                          </p>
                        )}
                      </div>
                      <div className="text-right">
                        <p className="font-bold text-gray-900">{formatBRL(valor)}</p>
                        {entrega.numeroPedido && (
                          <p className="text-xs text-gray-500">#{entrega.numeroPedido}</p>
                        )}
                      </div>
                    </div>
                  );
                }
              })}
            </div>
          )}
        </div>
      </main>

      {/* Menu Lateral */}
      {showMenu && (
        <>
          <div
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40 transition-opacity"
            onClick={() => setShowMenu(false)}
          />
          <div className="fixed inset-y-0 left-0 w-80 bg-white/95 backdrop-blur-xl shadow-2xl z-50 overflow-y-auto border-r border-gray-200/50 transform transition-transform">
            <div className="p-6 bg-gradient-to-br from-blue-600 to-indigo-700 text-white">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold">Menu</h2>
                <button
                  onClick={() => setShowMenu(false)}
                  className="p-2 hover:bg-white/20 rounded-lg transition-colors"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <div className="flex flex-col items-center">
                {empresaFoto ? (
                  <img
                    src={empresaFoto}
                    alt={empresaNome}
                    className="w-20 h-20 rounded-2xl border-4 border-white/30 shadow-xl mb-3 object-cover"
                  />
                ) : (
                  <div className="w-20 h-20 rounded-2xl bg-white/20 backdrop-blur-sm border-4 border-white/30 shadow-xl mb-3 flex items-center justify-center">
                    <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                    </svg>
                  </div>
                )}
                <p className="text-lg font-bold">{empresaNome || 'Empresa'}</p>
                <p className="text-sm text-blue-100 mt-1">Painel Administrativo</p>
              </div>
            </div>

            <nav className="p-4 space-y-2">
              <button
                onClick={() => {
                  setShowMenu(false);
                  navigate('/empresa');
                }}
                className="w-full text-left px-4 py-4 rounded-xl hover:bg-gradient-to-r hover:from-blue-50 hover:to-indigo-50 transition-all flex items-center gap-4 group"
              >
                <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform shadow-md">
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                  </svg>
                </div>
                <span className="font-semibold text-gray-700">Dashboard</span>
              </button>

              <button
                onClick={() => {
                  setShowMenu(false);
                  navigate('/status-pedido');
                }}
                className="w-full text-left px-4 py-4 rounded-xl hover:bg-gradient-to-r hover:from-blue-50 hover:to-indigo-50 transition-all flex items-center gap-4 group"
              >
                <div className="w-10 h-10 bg-gradient-to-br from-indigo-500 to-indigo-600 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform shadow-md">
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                  </svg>
                </div>
                <span className="font-semibold text-gray-700">Pedidos</span>
              </button>

              <button
                onClick={() => {
                  setShowMenu(false);
                  navigate('/perfil');
                }}
                className="w-full text-left px-4 py-4 rounded-xl hover:bg-gradient-to-r hover:from-blue-50 hover:to-indigo-50 transition-all flex items-center gap-4 group"
              >
                <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform shadow-md">
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                </div>
                <span className="font-semibold text-gray-700">Perfil</span>
              </button>

              <button
                onClick={() => {
                  setShowMenu(false);
                  navigate('/clientes');
                }}
                className="w-full text-left px-4 py-4 rounded-xl hover:bg-gradient-to-r hover:from-blue-50 hover:to-indigo-50 transition-all flex items-center gap-4 group"
              >
                <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-green-600 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform shadow-md">
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                  </svg>
                </div>
                <span className="font-semibold text-gray-700">Lista de Clientes</span>
              </button>

              <button
                onClick={() => {
                  setShowMenu(false);
                  navigate('/relatorio');
                }}
                className="w-full text-left px-4 py-4 rounded-xl bg-gradient-to-r from-blue-50 to-indigo-50 transition-all flex items-center gap-4 group"
              >
                <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform shadow-md">
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                </div>
                <span className="font-semibold text-purple-700">Histórico de Entregas</span>
              </button>

              <button
                onClick={() => {
                  setShowMenu(false);
                  navigate('/tabela-taxas');
                }}
                className="w-full text-left px-4 py-4 rounded-xl hover:bg-gradient-to-r hover:from-blue-50 hover:to-indigo-50 transition-all flex items-center gap-4 group"
              >
                <div className="w-10 h-10 bg-gradient-to-br from-amber-500 to-amber-600 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform shadow-md">
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <span className="font-semibold text-gray-700">Tabela de Taxas</span>
              </button>

              <button
                onClick={() => {
                  setShowMenu(false);
                  navigate('/configuracoes');
                }}
                className="w-full text-left px-4 py-4 rounded-xl hover:bg-gradient-to-r hover:from-blue-50 hover:to-indigo-50 transition-all flex items-center gap-4 group"
              >
                <div className="w-10 h-10 bg-gradient-to-br from-gray-500 to-gray-600 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform shadow-md">
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                </div>
                <span className="font-semibold text-gray-700">Configurações</span>
              </button>

              <div className="pt-4 mt-4 border-t border-gray-200">
                <button
                  onClick={() => {
                    setShowMenu(false);
                    handleLogout();
                  }}
                  className="w-full text-left px-4 py-4 rounded-xl hover:bg-gradient-to-r hover:from-red-50 hover:to-pink-50 transition-all flex items-center gap-4 group"
                >
                  <div className="w-10 h-10 bg-gradient-to-br from-red-500 to-red-600 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform shadow-md">
                    <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                    </svg>
                  </div>
                  <span className="font-semibold text-red-600">Sair</span>
                </button>
              </div>
            </nav>
          </div>
        </>
      )}
    </div>
  );
};

export default HistoricoEntregas;

